import React, { useMemo, useState } from 'react';
import {
  IconHelp,
  IconPlus,
  IconSearch,
  IconCalculator,
  IconChart,
  IconShield,
  IconStar,
  IconUser,
  IconZap,
} from '../components/Icons';

const SECTIONS = [
  {
    id: 'getting-started',
    icon: <IconZap size={16} />,
    eyebrow: 'Getting started',
    title: 'The basics',
    items: [
      {
        q: 'What is RiskLo?',
        a: 'RiskLo is a strategy risk assessment dashboard built for prop-firm and futures traders. It analyzes historical strategy data alongside your account configuration to tell you, before you trade, whether a setup is likely to blow your account or violate consistency rules.',
      },
      {
        q: 'Who is RiskLo for?',
        a: 'Funded prop-firm traders, systematic futures traders, and trading coaches who want their risk math automated and presented in plain English instead of a 90-minute spreadsheet session.',
      },
      {
        q: 'Do I need to install anything?',
        a: 'No. RiskLo runs entirely in the browser. Sign in with Google, Microsoft, or email and you have everything you need.',
      },
      {
        q: 'Can I try RiskLo without paying?',
        a: 'Yes. RiskLo Basic is free forever and includes Blowout Risk, % Rule Risk, Results, NQ ↔ MNQ scaling, and all of the educational guides.',
      },
    ],
  },
  {
    id: 'calculations',
    icon: <IconCalculator size={16} />,
    eyebrow: 'Risk calculations',
    title: 'How the math works',
    items: [
      {
        q: 'How does the Account Blowout Risk calculation work?',
        a: "RiskLo compares your historical worst loss (scaled by your contract count) against your trailing max drawdown. If the worst historical loss exceeds your drawdown, you see a NO GO with the gap. If it stays below, you see a GO and the buffer. Important: trailing drawdown is intraday, so end-of-day data may understate true risk — that disclaimer ships with every analysis.",
      },
      {
        q: 'What is the % Rule (consistency rule)?',
        a: 'The % Rule is a prop-firm rule that limits how much a single trading day can dominate your overall results. RiskLo supports 20%, 30%, 40%, 50%, and 60% rules, which cover the consistency rule sets used by the major prop firms.',
      },
      {
        q: 'How does RiskLo calculate the consistency limit?',
        a: 'RiskLo uses your Start-of-Day Profit Balance and Safety Net to derive a base amount (safety net when profit is lower, otherwise profit). It applies your selected percentage and compares the limit to historical worst losses scaled by your contracts.',
      },
      {
        q: "What's the difference between NQ and MNQ?",
        a: 'NQ (Nasdaq E-mini) is the full-size futures contract; MNQ (Micro E-mini Nasdaq) is 1/10th the size. Most strategy data is recorded on NQ — RiskLo automatically scales by 10x when you select MNQ so the math matches your real position size.',
      },
      {
        q: 'What does the Risk Score mean?',
        a: 'The Risk Score is a 0–100 summary that blends the historical worst loss as a percentage of account size with your drawdown usage. Lower is safer, higher is riskier. It is a quick read, not a guarantee.',
      },
    ],
  },
  {
    id: 'features',
    icon: <IconChart size={16} />,
    eyebrow: 'Features',
    title: 'What you can do with RiskLo',
    items: [
      {
        q: 'Can I analyze multiple accounts at once?',
        a: 'Yes — Bulk Risk Assessment is included in RiskLo Pro and lets you score up to 40 account + strategy combinations in parallel. Drag NinjaTrader CSV exports in and the grid populates itself.',
      },
      {
        q: 'What is Portfolio Simulator?',
        a: 'A Pro feature that lets you combine multiple strategies into a single sleeve and see drawdown, daily P&L, and consistency exposure across the whole book — not one strategy at a time.',
      },
      {
        q: 'What is the strategy dashboard?',
        a: 'Pro-level strategy tooling that runs your brand\'s strategy library against SPY, QQQ, IEF, ES, NQ, and YM benchmarks, scaled to your real capital base. You get growth curves, drawdowns, and period returns.',
      },
      {
        q: 'Can I customize my dashboard?',
        a: 'Yes — Custom Dashboards (Pro) let you drag-and-drop widgets like current results, risk metrics, and equity curves into a saved layout that matches the way you trade.',
      },
    ],
  },
  {
    id: 'pricing',
    icon: <IconStar size={16} />,
    eyebrow: 'Pricing & billing',
    title: 'Plans, trials, and payments',
    items: [
      {
        q: 'How much does RiskLo cost?',
        a: 'Basic is free forever. Pro is $30 / month, or $20 / month for members of a partner company such as Vector. Pro includes RiskLo AI, My Portfolio (up to 5 broker accounts), Blowout Risk, % Rule and Hedging Risk, the Portfolio Simulator, Bulk Risk, NinjaTrader CSV upload, the portfolio risk tools, custom dashboards and email summaries. A 7-day free trial is available.',
      },
      {
        q: 'How does the free trial work?',
        a: 'You can start a 7-day free trial of Pro at any time. Cancel before the trial ends and you will not be charged.',
      },
      {
        q: 'Who handles billing?',
        a: 'Pro subscriptions are billed through Whop. Whop handles cards, receipts, invoices, and cancellations — you manage your subscription directly from your account page.',
      },
      {
        q: 'Can I cancel anytime?',
        a: 'Yes. Cancel from your account page at any time. You keep Pro access through the end of the current billing period.',
      },
    ],
  },
  {
    id: 'data',
    icon: <IconShield size={16} />,
    eyebrow: 'Data & security',
    title: 'How RiskLo handles your information',
    items: [
      {
        q: 'Is my data secure?',
        a: 'Yes. RiskLo uses Google-verified OAuth where applicable, read-only access for spreadsheet data, encrypted transit, and never stores broker credentials. CSV uploads stay scoped to your account.',
      },
      {
        q: 'Does RiskLo place trades for me?',
        a: 'No. RiskLo is an analytics tool. It never executes trades, never connects with write access to a broker, and never holds credentials capable of trading.',
      },
      {
        q: 'Where does the strategy data come from?',
        a: 'Strategy data is provided by the brand that publishes each strategy, through a shared spreadsheet, CSV uploads, or live trade alerts, and is updated as new results come in. RiskLo transforms that data into grouped strategies, weekly P&L, and per-strategy historical performance.',
      },
      {
        q: 'How often is the data updated?',
        a: 'Strategy performance data is updated daily, so your risk assessment always reflects the latest available trading day.',
      },
    ],
  },
  {
    id: 'account',
    icon: <IconUser size={16} />,
    eyebrow: 'Account',
    title: 'Logging in and managing access',
    items: [
      {
        q: 'How do I sign in?',
        a: 'Use Google or Microsoft OAuth, or create an email + password account. We recommend Google for the smoothest experience.',
      },
      {
        q: 'I forgot my password. What do I do?',
        a: 'On the sign-in page, click "Forgot password" and we will email you a reset link.',
      },
      {
        q: 'Is RiskLo affiliated with any specific prop firm or data provider?',
        a: 'No. RiskLo is an independent analytics product. We model prop-firm rules accurately but are not a financial advisor and are not affiliated with any specific provider.',
      },
      {
        q: 'Should I rely on RiskLo for live trading decisions?',
        a: 'RiskLo is a risk-assessment tool, not financial advice. Use it as one input alongside your own judgment and your broker / prop-firm tools. Past performance does not guarantee future results.',
      },
    ],
  },
];

function highlight(text, query) {
  if (!query) return text;
  const re = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig');
  const parts = text.split(re);
  return parts.map((part, i) =>
    re.test(part) ? (
      <mark key={i} className="marketing-faq-mark">{part}</mark>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
}

function FaqItem({ q, a, query, forceOpen }) {
  const [open, setOpen] = useState(false);
  const isOpen = forceOpen || open;
  return (
    <div className={`marketing-faq-item ${isOpen ? 'is-open' : ''}`}>
      <button type="button" className="marketing-faq-q" onClick={() => setOpen((v) => !v)}>
        <span>{highlight(q, query)}</span>
        <IconPlus size={18} />
      </button>
      <div className="marketing-faq-a">
        <p>{highlight(a, query)}</p>
      </div>
    </div>
  );
}

function HelpPage({ onNavigate, user }) {
  const go = (page) => () => onNavigate && onNavigate(page);
  const isSignedIn = Boolean(user?.email);
  const [query, setQuery] = useState('');

  const trimmedQuery = query.trim();
  const filtered = useMemo(() => {
    if (!trimmedQuery) return SECTIONS;
    const q = trimmedQuery.toLowerCase();
    return SECTIONS.map((section) => ({
      ...section,
      items: section.items.filter(
        (item) =>
          item.q.toLowerCase().includes(q) || item.a.toLowerCase().includes(q)
      ),
    })).filter((section) => section.items.length > 0);
  }, [trimmedQuery]);

  const totalMatches = filtered.reduce((sum, s) => sum + s.items.length, 0);

  return (
    <>
      <section className="marketing-page-header">
        <div className="marketing-container marketing-container-narrow">
          <div className="marketing-eyebrow marketing-reveal">
            <IconHelp size={14} /> Help center
          </div>
          <h1 className="marketing-title marketing-reveal" data-delay="1">
            Frequently asked questions.
          </h1>
          <p className="marketing-subtitle marketing-reveal" data-delay="2" style={{ margin: '1rem auto 0' }}>
            Everything you need to know about how RiskLo works — from the math behind blowout
            probability to billing, security, and account management.
          </p>

          <div className="marketing-faq-search marketing-reveal" data-delay="3">
            <span className="marketing-faq-search-icon"><IconSearch size={18} /></span>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the FAQ…"
              aria-label="Search the FAQ"
            />
            {query ? (
              <button
                type="button"
                className="marketing-faq-search-clear"
                onClick={() => setQuery('')}
                aria-label="Clear search"
              >
                Clear
              </button>
            ) : null}
          </div>

          {trimmedQuery ? (
            <p className="marketing-faq-result-count marketing-reveal" data-delay="3">
              {totalMatches === 0
                ? `No matches for “${trimmedQuery}”.`
                : `${totalMatches} match${totalMatches === 1 ? '' : 'es'} for “${trimmedQuery}”.`}
            </p>
          ) : (
            <div className="marketing-chip-row marketing-reveal" data-delay="3" style={{ marginTop: '1.5rem' }}>
              {SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="marketing-chip"
                  onClick={(e) => {
                    e.preventDefault();
                    const el = document.getElementById(s.id);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  {s.icon} {s.eyebrow}
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="marketing-section" style={{ paddingTop: 0 }}>
        <div className="marketing-container marketing-container-narrow">
          {filtered.length === 0 ? (
            <div className="marketing-faq-empty">
              <IconSearch size={28} />
              <h3>No questions matched your search.</h3>
              <p>Try a shorter or different keyword — or reach out and we'll get you an answer.</p>
              <button type="button" className="marketing-btn-ghost" onClick={go('contact')}>
                Contact support
              </button>
            </div>
          ) : (
            filtered.map((section) => (
              <div
                key={section.id}
                id={section.id}
                style={{
                  scrollMarginTop: '5rem',
                  marginBottom: 'clamp(2.5rem, 5vw, 4rem)',
                }}
              >
                <div className="marketing-section-heading marketing-reveal" style={{ marginBottom: '1.5rem' }}>
                  <div className="marketing-eyebrow">
                    {section.icon} {section.eyebrow}
                  </div>
                  <h2
                    className="marketing-title"
                    style={{ fontSize: 'clamp(1.6rem, 3.2vw, 2.2rem)', margin: '0.75rem 0 0' }}
                  >
                    {section.title}
                  </h2>
                </div>
                <div className="marketing-faq">
                  {section.items.map((item, i) => (
                    <div key={item.q} className="marketing-reveal" data-delay={Math.min(i + 1, 5)}>
                      <FaqItem
                        q={item.q}
                        a={item.a}
                        query={trimmedQuery}
                        forceOpen={Boolean(trimmedQuery)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <section className="marketing-section">
        <div className="marketing-container">
          <div className="marketing-cta marketing-reveal">
            <div className="marketing-cta-inner">
              <div className="marketing-eyebrow"><IconHelp size={14} /> Still have questions?</div>
              <h2>We're happy to help.</h2>
              <p>
                Reach out and we'll get back to you within a business day — or sign in and try RiskLo
                yourself.
              </p>
              <div className="marketing-hero-cta-row" style={{ marginTop: 0 }}>
                <button type="button" className="marketing-btn marketing-btn-lg" onClick={go(isSignedIn ? 'results' : 'login')}>
                  {isSignedIn ? 'Open dashboard' : 'Sign in free'}
                </button>
                <button type="button" className="marketing-btn-ghost marketing-btn-lg" onClick={go('contact')}>
                  Contact us
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default HelpPage;
